import {
	Box,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableCellProps,
	TableContainer,
	TableHead,
	TableRow,
	tableCellClasses,
} from '@mui/material'
import { ReactComponent as EditLogo } from 'assets/table_edit_logo.svg'
import { ReactComponent as TrashLogo } from 'assets/trash_logo.svg'
import eyeIcon from 'assets/Eye.png'
import TreatmentEditDrawer from 'components/cattles/treatmentPage/components/drawers/TreatmentEditDrawer'
import TreatmentViewDrawer from 'components/cattles/treatmentPage/components/drawers/TreatmentViewDrawer'
import DeleteTreatmentSchemaModal from 'components/cattles/treatmentPage/components/modals/delete-modal/DeleteTreatmentSchemaModal'
import { TreatmentFormFields } from 'components/cattles/treatmentPage/types/types'
import {
	formatTreatmentDuration,
	getExaminationInterval,
	getTreatmentSchemaDiagnoses,
	getTreatmentSchemaName,
	resolveDiagnoseTitles,
	truncateWithEllipsis,
	TREATMENT_SCHEMA_DESCRIPTION_DISPLAY_LENGTH,
	TREATMENT_SCHEMA_DIAGNOSIS_MAX_LENGTH,
	TREATMENT_SCHEMA_TITLE_DISPLAY_LENGTH,
} from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import { useTreatmentMutation } from 'hook/mutations/useTreatmentSchemasMutations'
import { useGetTreatmentSchemas } from 'hook/query/useGetTreatmentSchemas'
import { ITreatmentSchema } from 'models/ICattlesData'
import { ReactNode, useEffect, useState } from 'react'
import { fetchDiagnosesList } from 'store/cattlesSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import Preloader from 'ui/Preloader'
import CustomTableRow from 'ui/table/CustomTableRow'
import TooltipHover from 'ui/TooltipHover'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'

const PAGE_SIZE = 10
const COLUMN_COUNT = 5
const ACTION_ICON_SIZE = 24
const ACTIONS_COL_WIDTH = '128px'
const TABLE_BODY_MIN_HEIGHT = `${PAGE_SIZE * 50}px`

const COL_WIDTH = {
	title: '18%',
	scheme: '40%',
	diagnosis: '20%',
	days: '14%',
	actions: ACTIONS_COL_WIDTH,
}

const actionsCellSx = {
	width: ACTIONS_COL_WIDTH,
	minWidth: ACTIONS_COL_WIDTH,
	maxWidth: ACTIONS_COL_WIDTH,
	overflow: 'visible',
	whiteSpace: 'nowrap',
} as const

const WrapCellText = ({
	children,
	keepNewlines = false,
}: {
	children: ReactNode
	keepNewlines?: boolean
}) => (
	<TypographyPrimary
		sx={{
			display: 'block',
			width: '100%',
			minWidth: 0,
			whiteSpace: keepNewlines ? 'pre-wrap' : 'normal',
			overflowWrap: 'anywhere',
			wordBreak: 'break-word',
		}}
	>
		{children}
	</TypographyPrimary>
)

const TwoLineHeader = ({
	first,
	second,
}: {
	first: string
	second: string
}) => (
	<TypographySecondary sx={{ whiteSpace: 'pre-line' }}>
		{`${first}\n${second}`}
	</TypographySecondary>
)

const getEditData = (
	item: ITreatmentSchema,
): TreatmentFormFields & { id: number } => ({
	id: item.id,
	title: item.title || '',
	description: item.description,
	duration: String(item.duration ?? ''),
	selectedDiagnose: getTreatmentSchemaDiagnoses(item)
		.map(diagnose => diagnose.id)
		.filter(id => id > 0),
	examinationInterval: (() => {
		const interval = getExaminationInterval(item)
		return interval != null && Number(interval) >= 1 ? String(interval) : ''
	})(),
	rejectMilk: Boolean(item.milk_reject_days),
	milkRejectDays:
		item.milk_reject_days != null ? String(item.milk_reject_days) : '',
})

interface TreatmentTableProps {
	headerAction?: ReactNode
}

export const TreatmentTable = ({ headerAction }: TreatmentTableProps) => {
	const dispatch = useAppDispatch()
	const diagnosesList =
		useAppSelector(state => state.cattlesSlice.diagnosesList) ?? []
	const { data, isLoading } = useGetTreatmentSchemas()
	const { deleteTreatmentSchema } = useTreatmentMutation()
	const [visibleSchemas, setVisibleSchemas] = useState<ITreatmentSchema[]>([])
	const schemas = data ?? []

	useEffect(() => {
		if (diagnosesList.length === 0) {
			dispatch(fetchDiagnosesList())
		}
	}, [dispatch, diagnosesList.length])

	const head = {
		rowKey: 'treatment-header',
		rowBorderBottom: COLORS.secondaryFontOpacity,
		rowHeight: '48px',
		cellItems: [
			{
				width: COL_WIDTH.title,
				verticalAlign: 'center',
				children: <TwoLineHeader first='Наименование' second='схемы лечения' />,
			},
			{
				width: COL_WIDTH.scheme,
				verticalAlign: 'center',
				children: <TypographySecondary>Схема лечения</TypographySecondary>,
			},
			{
				width: COL_WIDTH.diagnosis,
				verticalAlign: 'center',
				children: <TwoLineHeader first='Наименование' second='диагноза' />,
			},
			{
				width: COL_WIDTH.days,
				verticalAlign: 'center',
				children: <TypographySecondary>Лечение, дни</TypographySecondary>,
			},
			{
				align: 'left' as TableCellProps['align'],
				verticalAlign: 'center',
				width: COL_WIDTH.actions,
				sx: actionsCellSx,
				children: <TypographySecondary>Действия</TypographySecondary>,
			},
		],
	}
	const [editData, setEditData] = useState<Partial<
		TreatmentFormFields & { id: number }
	> | null>(null)
	const [viewSchema, setViewSchema] = useState<ITreatmentSchema | null>(null)
	const [deleteId, setDeleteId] = useState<number | null>(null)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const selectedSchema = schemas.find(item => item.id === deleteId)

	const rows = visibleSchemas.length
		? visibleSchemas
		: schemas.slice(0, PAGE_SIZE)

	return (
		<CardStandart
			sx={{
				padding: '24px',
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '16px',
				}}
			>
				<TypographyHeader>Схемы лечения</TypographyHeader>
				{headerAction}
			</Box>

			<TableContainer sx={{ overflow: 'hidden' }}>
				<Table
					sx={{
						tableLayout: 'fixed',
						width: '100%',
						'& .MuiTableCell-root:not(:last-of-type)': { overflow: 'hidden' },
						'& .MuiTableRow-root .MuiTableCell-root:nth-of-type(5)': {
							width: ACTIONS_COL_WIDTH,
							maxWidth: ACTIONS_COL_WIDTH,
						},
					}}
				>
					<TableHead
						sx={{
							[`& .${tableCellClasses.root}`]: { backgroundColor: '#fff' },
						}}
					>
						<CustomTableRow {...head} />
					</TableHead>

					<TableBody>
						{isLoading ? (
							<TableRow sx={{ height: TABLE_BODY_MIN_HEIGHT }}>
								<TableCell colSpan={COLUMN_COUNT} align='center'>
									<Preloader />
								</TableCell>
							</TableRow>
						) : !schemas.length ? (
							<TableRow sx={{ height: TABLE_BODY_MIN_HEIGHT }}>
								<TableCell
									colSpan={COLUMN_COUNT}
									align='center'
									sx={{ p: 0, borderBottom: 'none' }}
								>
									<TypographySecondary>
										Схемы лечения отсутствуют
									</TypographySecondary>
								</TableCell>
							</TableRow>
						) : (
							rows.map(item => {
								const diagnoses = resolveDiagnoseTitles(
									getTreatmentSchemaDiagnoses(item),
									diagnosesList,
								).filter(diagnose => diagnose.title)

								return (
									<CustomTableRow
										key={item.id}
										rowKey={`treatment-row-${item.id}`}
										rowHeight='auto'
										cellItems={[
											{
												width: COL_WIDTH.title,
												padding: '12px 16px 12px 0',
												verticalAlign: 'center',
												children: (
													<WrapCellText>
														{truncateWithEllipsis(
															item.title,
															TREATMENT_SCHEMA_TITLE_DISPLAY_LENGTH,
														)}
													</WrapCellText>
												),
											},
											{
												width: COL_WIDTH.scheme,
												padding: '12px 16px 12px 0',
												verticalAlign: 'center',
												children: (
													<WrapCellText keepNewlines>
														{truncateWithEllipsis(
															item.description,
															TREATMENT_SCHEMA_DESCRIPTION_DISPLAY_LENGTH,
														)}
													</WrapCellText>
												),
											},
											{
												width: COL_WIDTH.diagnosis,
												padding: '12px 16px 12px 0',
												verticalAlign: 'center',
												children: (
													<Box
														sx={{
															display: 'flex',
															flexDirection: 'column',
															gap: '4px',
															minWidth: 0,
															width: '100%',
														}}
													>
														{diagnoses.length ? (
															diagnoses.map((diagnose, diagnoseIndex) => (
																<WrapCellText
																	key={`${item.id}-${diagnose.id}-${diagnose.title}`}
																>
																	{`${truncateWithEllipsis(
																		diagnose.title,
																		TREATMENT_SCHEMA_DIAGNOSIS_MAX_LENGTH,
																	)}${
																		diagnoseIndex < diagnoses.length - 1
																			? ','
																			: ''
																	}`}
																</WrapCellText>
															))
														) : (
															<TypographyPrimary>
																Без диагноза
															</TypographyPrimary>
														)}
													</Box>
												),
											},
											{
												width: COL_WIDTH.days,
												padding: '12px 16px 12px 0',
												verticalAlign: 'center',
												children: (
													<TypographyPrimary sx={{ whiteSpace: 'nowrap' }}>
														{formatTreatmentDuration(item.duration)}
													</TypographyPrimary>
												),
											},
											{
												width: COL_WIDTH.actions,
												align: 'left',
												padding: '12px 0',
												verticalAlign: 'center',
												sx: actionsCellSx,
												children: (
													<Box
														sx={{
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'flex-start',
															gap: '8px',
															flexShrink: 0,
															width: 'max-content',
														}}
													>
														<TooltipHover
															title='Смотреть схему'
															placement='bottom'
															oneLine
														>
															<img
																src={eyeIcon}
																alt=''
																style={{
																	width: ACTION_ICON_SIZE,
																	height: ACTION_ICON_SIZE,
																	display: 'block',
																	cursor: 'pointer',
																}}
																onClick={() => setViewSchema(item)}
															/>
														</TooltipHover>
														<TooltipHover
															title='Редактировать схему'
															placement='bottom'
															oneLine
														>
															<EditLogo
																style={{
																	width: ACTION_ICON_SIZE,
																	height: ACTION_ICON_SIZE,
																	cursor: 'pointer',
																}}
																onClick={() => {
																	setEditData(getEditData(item))
																}}
															/>
														</TooltipHover>
														<TooltipHover
															title='Удалить схему'
															placement='bottom-end'
															oneLine
														>
															<TrashLogo
																style={{
																	width: ACTION_ICON_SIZE,
																	height: ACTION_ICON_SIZE,
																	cursor: 'pointer',
																}}
																onClick={() => {
																	setDeleteId(item.id)
																	setIsDeleteModalOpen(true)
																}}
															/>
														</TooltipHover>
													</Box>
												),
											},
										]}
									/>
								)
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{!isLoading && schemas.length > 0 && (
				<>
					<Divider />
					<CustomPagination
						initialArray={schemas}
						setVisibleState={setVisibleSchemas}
						count={PAGE_SIZE}
					/>
				</>
			)}
			<TreatmentEditDrawer
				open={Boolean(editData)}
				editData={editData}
				onClose={() => setEditData(null)}
			/>
			<TreatmentViewDrawer
				open={Boolean(viewSchema)}
				schema={viewSchema}
				onClose={() => setViewSchema(null)}
				onEdit={() => {
					if (!viewSchema) return
					setEditData(getEditData(viewSchema))
					setViewSchema(null)
				}}
			/>
			<DeleteTreatmentSchemaModal
				open={isDeleteModalOpen}
				schemaName={
					selectedSchema ? getTreatmentSchemaName(selectedSchema) : ''
				}
				diagnosisNames={
					selectedSchema
						? getTreatmentSchemaDiagnoses(selectedSchema).map(
								diagnose => diagnose.title,
							)
						: []
				}
				onClose={() => {
					setIsDeleteModalOpen(false)
					setDeleteId(null)
				}}
				onConfirm={() => {
					if (deleteId === null) return

					deleteTreatmentSchema.mutate(deleteId, {
						onSettled: () => {
							setIsDeleteModalOpen(false)
							setDeleteId(null)
						},
					})
				}}
			/>
		</CardStandart>
	)
}
